import dayjs from 'dayjs';
import type { DatePreset, Granularity, Platform } from './socialMediaConstants';

/**
 * The filter every analytics query is keyed on.
 *
 * Dates are ISO strings, not Date objects, for two reasons: pageFiltersStore persists
 * through JSON.stringify (a Date comes back as a string and would crash the picker on
 * the next reload), and react-query hashes Date instances unstably, which would make the
 * cache key change on every render.
 */
export interface AnalyticsFilter {
  preset: DatePreset;
  /** Inclusive, YYYY-MM-DD. */
  from: string;
  /** Inclusive, YYYY-MM-DD. */
  to: string;
  granularity: Granularity;
  /** Empty = all platforms. Kept sorted so ['FB','IG'] and ['IG','FB'] share a cache entry. */
  platforms: Platform[];
  /** Empty = all accounts. Kept sorted for the same reason. */
  accountIds: string[];
  compareToPrevious: boolean;
}

export const DEFAULT_FILTER: AnalyticsFilter = {
  preset: 'last28',
  ...resolvePreset('last28'),
  granularity: 'day',
  platforms: [],
  accountIds: [],
  compareToPrevious: true,
};

/**
 * Resolves a preset to concrete dates.
 *
 * Called on mount as well as on change, so a stored preset stays rolling rather than
 * pinned to the day it was saved.
 */
export function resolvePreset(preset: DatePreset, existing?: { from: string; to: string }) {
  const today = dayjs().startOf('day');

  switch (preset) {
    case 'last7':
      return { from: today.subtract(6, 'day').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'last28':
      return { from: today.subtract(27, 'day').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'last90':
      return { from: today.subtract(89, 'day').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'thisMonth':
      return { from: today.startOf('month').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'lastMonth': {
      const start = today.subtract(1, 'month').startOf('month');
      return { from: start.format('YYYY-MM-DD'), to: start.endOf('month').format('YYYY-MM-DD') };
    }
    case 'ytd':
      return { from: today.startOf('year').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'custom':
    default:
      // A custom range is whatever the user picked; fall back to last 28 days.
      return existing ?? {
        from: today.subtract(27, 'day').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
  }
}

/** Inclusive length of the window, in days. */
export function rangeLengthDays(filter: Pick<AnalyticsFilter, 'from' | 'to'>): number {
  return dayjs(filter.to).diff(dayjs(filter.from), 'day') + 1;
}

/**
 * The equivalent window immediately before this one, for period-over-period deltas.
 *
 * Same length, ending the day before `from`, so "last 28 days" compares against the 28
 * days before that rather than a calendar month.
 */
export function previousPeriod(filter: AnalyticsFilter): AnalyticsFilter {
  const length = rangeLengthDays(filter);
  const to = dayjs(filter.from).subtract(1, 'day');
  return {
    ...filter,
    preset: 'custom',
    from: to.subtract(length - 1, 'day').format('YYYY-MM-DD'),
    to: to.format('YYYY-MM-DD'),
    compareToPrevious: false,
  };
}

export interface MetricDelta {
  current: number;
  previous?: number;
  change?: number;
  changePercent?: number;
  /** Undefined when there is nothing to compare against. */
  direction?: 'up' | 'down' | 'flat';
}

/**
 * Period-over-period delta.
 *
 * A previous value of 0 yields no percentage: "up ∞%" is worse than showing nothing,
 * and 0 -> 5 is not a meaningful percentage increase.
 */
export function computeDelta(current: number, previous?: number): MetricDelta {
  if (previous === undefined || previous === null) return { current };

  const change = current - previous;
  const changePercent = previous === 0 ? undefined : (change / Math.abs(previous)) * 100;

  return {
    current,
    previous,
    change,
    changePercent,
    direction: change > 0 ? 'up' : change < 0 ? 'down' : 'flat',
  };
}

/**
 * How a metric aggregates over a period. Getting this wrong is the classic BI error.
 *
 *  sum  - reach, impressions, views, engagements: additive across days
 *  last - follower count: a LEVEL, not a flow. Summing it across 28 days multiplies
 *         your audience by 28.
 *  rate - engagement rate, CTR: must be recomputed from the summed parts, never
 *         averaged. AVG of daily rates weights a quiet day the same as a busy one.
 */
export type AggregateMode = 'sum' | 'last' | 'avg' | 'rate';

export function aggregate<T>(rows: T[], key: keyof T, mode: AggregateMode = 'sum'): number {
  if (rows.length === 0) return 0;
  const values = rows.map((r) => Number(r[key] ?? 0)).filter((n) => Number.isFinite(n));
  if (values.length === 0) return 0;

  switch (mode) {
    case 'last':
      return values[values.length - 1];
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'sum':
    case 'rate':
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

/** A rate over a period: SUM(numerator) / SUM(denominator), never an average of rates. */
export function computeRate<T>(rows: T[], numerator: keyof T, denominator: keyof T): number {
  const n = aggregate(rows, numerator, 'sum');
  const d = aggregate(rows, denominator, 'sum');
  return d > 0 ? (n / d) * 100 : 0;
}

/** 8_543 -> "8.5K". Compact, for KPI tiles. */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(Math.round(value));
}

export function formatPercent(value: number | null | undefined, dp = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(dp)}%`;
}

export function formatMoney(value: number | null | undefined, currency = 'R'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${currency}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Human label for the current window, used in headings and export filenames. */
export function periodLabel(filter: Pick<AnalyticsFilter, 'from' | 'to'>): string {
  return `${dayjs(filter.from).format('D MMM YYYY')} – ${dayjs(filter.to).format('D MMM YYYY')}`;
}

/**
 * Normalises a filter so equivalent filters hash to the same react-query key.
 * Without the sort, reordering a multi-select would refetch identical data.
 */
export function normaliseFilter(filter: AnalyticsFilter): AnalyticsFilter {
  return {
    ...filter,
    platforms: [...filter.platforms].sort(),
    accountIds: [...filter.accountIds].sort(),
  };
}
